import React, { useState } from 'react';
import { faChevronDown, faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { SearchField } from 'elements/SearchField/SearchField';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { useMemoFormContext } from 'elements/MemoForm/context/FormContext';
import { MemoType } from 'models/Memo';
import { useTranslations } from 'next-intl';
import {
  MEMO_TYPE_ICON,
  MEMO_TYPE_OUTLINE_ICON,
} from 'elements/Memo/memoTypeIcon';
import { COMPOSER_TYPES } from '../composerTypes';
import { useSwitchMemoType } from '../hooks/useSwitchMemoType';

interface Props {
  // `pill` is the header control: the current type's icon and name with a
  // chevron. `more` is the "…" button that ends the resting row's quick
  // types.
  variant?: 'pill' | 'more';
  // Called after every pick, even when the user picks the current type.
  onTypeSelected?: () => void;
}

/**
 * The composer's type control. Clicking it opens a searchable grid of every
 * memo type, each with a one-line description.
 */
export const MemoTypeSelectorForm: React.FC<Props> = ({
  variant = 'pill',
  onTypeSelected,
}) => {
  const t = useTranslations();
  const { selectedMemoType } = useMemoFormContext();
  const switchType = useSwitchMemoType();
  const [query, setQuery] = useState('');

  const filteredTypes =
    query.trim() === ''
      ? COMPOSER_TYPES
      : COMPOSER_TYPES.filter((type) => {
          const needle = query.trim().toLowerCase();
          return (
            t(`memoTypes.${type}`).toLowerCase().includes(needle) ||
            t(`memoTypeSubtitles.${type}`).toLowerCase().includes(needle)
          );
        });

  const handleTypeChange = (memoType: MemoType) => {
    setQuery('');
    switchType(memoType);
    onTypeSelected?.();
  };

  const resultsLabel =
    filteredTypes.length === 1
      ? t('memoComposer.typePicker.resultsOne')
      : t('memoComposer.typePicker.resultsMany', {
          count: filteredTypes.length,
        });

  return (
    <Listbox value={selectedMemoType} onChange={handleTypeChange}>
      {variant === 'pill' ? (
        <ListboxButton
          onClick={() => setQuery('')}
          className={styles.pill.btn}
          data-testid="memo-type-badge"
          title={t('memoComposer.changeType')}
        >
          <span className={styles.pill.circle}>
            <FontAwesomeIcon
              icon={MEMO_TYPE_ICON[selectedMemoType]}
              className={styles.pill.icon}
              aria-hidden="true"
            />
          </span>
          <span className={styles.pill.label}>
            {t(`memoTypes.${selectedMemoType}`)}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={styles.pill.chevron}
            aria-hidden="true"
          />
        </ListboxButton>
      ) : (
        <ListboxButton
          onClick={() => setQuery('')}
          className={styles.more.btn}
          data-testid="memo-type-more"
          title={t('memoComposer.moreTypes')}
          aria-label={t('memoComposer.moreTypes')}
        >
          <FontAwesomeIcon icon={faEllipsis} aria-hidden="true" />
        </ListboxButton>
      )}

      <ListboxOptions
        anchor={{
          to: variant === 'pill' ? 'bottom start' : 'bottom end',
          gap: '6px',
        }}
        className={styles.panel.container}
      >
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder={t('memoComposer.typePicker.search')}
          trailing={
            <span className={styles.panel.results}>{resultsLabel}</span>
          }
        />

        <div className={styles.panel.grid}>
          {filteredTypes.map((type) => (
            <ListboxOption
              key={type}
              value={type}
              data-testid={`memo-type-option-${type}`}
              className={styles.panel.option.container}
            >
              <div className={styles.panel.option.icon}>
                <FontAwesomeIcon
                  icon={MEMO_TYPE_OUTLINE_ICON[type]}
                  aria-hidden="true"
                />
              </div>
              <div className={styles.panel.option.text}>
                <span className={styles.panel.option.label}>
                  {t(`memoTypes.${type}`)}
                </span>
                <span className={styles.panel.option.subtitle}>
                  {t(`memoTypeSubtitles.${type}`)}
                </span>
              </div>
            </ListboxOption>
          ))}
        </div>

        {filteredTypes.length === 0 && (
          <div className={styles.panel.empty}>{t('common.noMatches')}</div>
        )}
      </ListboxOptions>
    </Listbox>
  );
};

const styles = {
  pill: {
    btn: classNames(
      'inline-flex shrink-0 items-center gap-2 rounded-full border border-stroke bg-surface-card',
      'py-[3px] pl-[3px] pr-2.5 hover:bg-surface-field focus:outline-hidden',
      'focus-visible:ring-2 focus-visible:ring-accent-ink/40',
    ),
    circle:
      'flex size-6 items-center justify-center rounded-full bg-accent-fill text-white',
    icon: 'text-[11px]',
    label: 'text-[13px] font-semibold text-ink-primary',
    chevron: 'text-[9px] text-ink-secondary',
  },
  more: {
    btn: classNames(
      'flex size-8 shrink-0 items-center justify-center rounded-lg text-[15px] text-ink-primary',
      'hover:bg-ink-secondary/10 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ink/40',
      'data-open:bg-ink-secondary/10',
    ),
  },
  panel: {
    container: classNames(
      'z-[60] w-[470px] max-h-[420px] overflow-y-auto rounded-xl bg-surface-overlay shadow-lg',
      'ring-1 ring-hairline focus:outline-hidden',
    ),
    results: 'shrink-0 text-[11px] text-ink-tertiary',
    // Fills down the left column first, then the right.
    grid: 'grid grid-flow-col grid-cols-2 grid-rows-5 gap-1 p-2',
    option: {
      container: classNames(
        'group flex cursor-pointer select-none items-center gap-3 rounded-[10px] border border-transparent px-2.5 py-2',
        'data-focus:bg-accent-tint data-selected:border-accent-tint-ink/30 data-selected:bg-accent-tint',
      ),
      icon: classNames(
        'flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-field text-[15px] text-ink-primary',
        'group-data-selected:bg-accent-fill group-data-selected:text-white',
      ),
      text: 'flex min-w-0 flex-col',
      label: 'truncate text-sm font-semibold text-ink-primary',
      subtitle: 'truncate text-xs text-ink-secondary',
    },
    empty: 'px-3.5 pb-3 pt-1 text-xs text-ink-tertiary',
  },
};
